import React from 'react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { getDoctorColor } from './calendarUtils';

dayjs.extend(isoWeek);

const MAX_EVENTS_VISIBLE = 4;
const UNASSIGNED_COLOR = '#94a3b8';

export default function CalendarGridView({
    anchorDate,
    events,
    colorMap,
    onEventClick,
    onDaySelect,
    onAddOnDay,
}) {
    const { t } = useTranslation();
    const start = anchorDate.startOf('month').startOf('isoWeek');
    const end = anchorDate.endOf('month').endOf('isoWeek');
    const cells = [];
    let cursor = start;
    while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
        cells.push(cursor);
        cursor = cursor.add(1, 'day');
    }

    return (
        // `overflow-x: auto` also makes `overflow-y` compute to `auto`, so keep a
        // little vertical padding here and no lifting effects inside.
        <div className="overflow-x-auto px-2.5 py-2.5 sm:px-3 sm:py-3">
            <div className="min-w-[720px] md:min-w-0">
                <div className="grid grid-cols-7 gap-px overflow-hidden rounded-t-lg bg-slate-200">
                    {[
                        t('calendar.dowMon'),
                        t('calendar.dowTue'),
                        t('calendar.dowWed'),
                        t('calendar.dowThu'),
                        t('calendar.dowFri'),
                        t('calendar.dowSat'),
                        t('calendar.dowSun'),
                    ].map((d) => (
                        <div
                            key={d}
                            className="bg-slate-50 py-2 text-center text-xs font-semibold text-slate-500"
                        >
                            {d}
                        </div>
                    ))}
                </div>
                <div className="mt-px grid grid-cols-7 gap-px overflow-hidden rounded-b-lg bg-slate-200">
                    {cells.map((day) => {
                        const inMonth = day.month() === anchorDate.month();
                        const isToday = day.isSame(dayjs(), 'day');
                        const dayEvents = events.filter((e) => dayjs(e.date).isSame(day, 'day'));
                        const visible = dayEvents.slice(0, MAX_EVENTS_VISIBLE);
                        const rest = dayEvents.length - visible.length;

                        return (
                            <div
                                key={day.format('YYYY-MM-DD')}
                                className={`flex min-h-[100px] flex-col p-1.5 md:min-h-[120px] ${
                                    isToday ? 'bg-[#1D2C7B]/[0.04]' : 'bg-white'
                                } ${!inMonth ? 'opacity-45' : ''}`}
                            >
                                <div className="mb-1 flex items-center justify-between gap-1">
                                    <button
                                        type="button"
                                        onClick={() => onDaySelect(day)}
                                        className={`h-7 w-7 rounded-md text-sm font-semibold tabular-nums transition-colors ${
                                            isToday
                                                ? 'bg-[#1D2C7B] text-white'
                                                : 'text-slate-700 hover:bg-slate-100'
                                        }`}
                                    >
                                        {day.format('D')}
                                    </button>
                                    {inMonth && onAddOnDay && (
                                        <button
                                            type="button"
                                            onClick={() => onAddOnDay(day)}
                                            className="rounded-md p-0.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-[#1D2C7B]"
                                            title={t('calendar.add')}
                                        >
                                            <PlusIcon className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                                <div className="flex-1 space-y-0.5 overflow-hidden">
                                    {visible.map((ev) => {
                                        const color = ev.doctor_id != null
                                            ? getDoctorColor(ev.doctor_id, colorMap)
                                            : UNASSIGNED_COLOR;
                                        return (
                                            <button
                                                key={ev.id}
                                                type="button"
                                                onClick={() => onEventClick(ev)}
                                                className="w-full truncate rounded-md px-1.5 py-1 text-left text-[11px] font-medium leading-tight text-white transition-opacity hover:opacity-90"
                                                style={{ backgroundColor: color }}
                                                title={ev.doctor?.name ? `${ev.title} · ${ev.doctor.name}` : ev.title}
                                            >
                                                {ev.title}
                                            </button>
                                        );
                                    })}
                                    {rest > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => onDaySelect(day)}
                                            className="px-1 text-[11px] font-semibold text-[#1D2C7B] tabular-nums"
                                        >
                                            {t('calendar.moreCount', { count: rest })}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
